import { get, ref } from 'firebase/database';
import React, { useState, useEffect } from 'react';
import { ModalContainer, ModalContent, Form, Label, Button, Input, Select, Option, OptionGroup } from './StyledComponents';
import { database } from '../firebase/firebaseConfig';
import AddAccountModal from './AddAccountModal';
import { addAccount } from '../firebase/firebaseUtils';

const EditTransactionModal = ({
  transaction,
  isOpen,
  onClose,
  onSave,
  accounts,
  userId,
  allowPaidStatusEdit = false,
  forceSingleEdit = false,
}) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [date, setDate] = useState('');
  const [recurrence, setRecurrence] = useState('');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [editMode, setEditMode] = useState('single');
  const [selectedOccurrenceDate, setSelectedOccurrenceDate] = useState('');
  const [firstUnpaidOccurrenceDate, setFirstUnpaidOccurrenceDate] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [activeAccountField, setActiveAccountField] = useState(null); // 'from' or 'to'

  useEffect(() => {
    const fetchParentData = async (parentId) => {
      const parentTransactionRef = ref(database, `users/${userId}/transactions/${parentId}`);
      const parentSnapshot = await get(parentTransactionRef);
      return parentSnapshot.val();
    };

    if (transaction) {
      setDescription(transaction.description || '');
      setAmount(transaction.amount || '');
      setFromAccount(transaction.fromAccount || '');
      setToAccount(transaction.toAccount || '');
      const transactionDate = transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : '';
      setSelectedOccurrenceDate(transactionDate);
      setFirstUnpaidOccurrenceDate('');
      setDate(transactionDate);
      setIsPaid(Boolean(transaction.isPaid));

      setEditMode('single');

      if (transaction.parentId && !forceSingleEdit) {
        fetchParentData(transaction.parentId).then(parentData => {
          if (parentData) {
            setRecurrence(parentData.recurrence || '');
            setRecurrenceEndDate(parentData.recurrenceEndDate ? new Date(parentData.recurrenceEndDate).toISOString().split('T')[0] : '');
            setRecurrenceInterval(parentData.recurrenceInterval || 1);
            if (parentData.childTransactions) {
              const oldestUnpaid = Object.values(parentData.childTransactions)
                .filter(c => !c.isPaid)
                .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
              if (oldestUnpaid) {
                setFirstUnpaidOccurrenceDate(new Date(oldestUnpaid.date).toISOString().split('T')[0]);
              }
            }
          }
        });
      } else {
        setRecurrence(transaction.recurrence || '');
        setRecurrenceEndDate(transaction.recurrenceEndDate ? new Date(transaction.recurrenceEndDate).toISOString().split('T')[0] : '');
        setRecurrenceInterval(transaction.recurrenceInterval || 1);
      }
    }
  }, [transaction, userId, forceSingleEdit]);

  useEffect(() => {
    if (!transaction?.parentId || forceSingleEdit) return;
    if (editMode === 'series' && firstUnpaidOccurrenceDate) {
      setDate(firstUnpaidOccurrenceDate);
    } else if (editMode === 'single' && selectedOccurrenceDate) {
      setDate(selectedOccurrenceDate);
    }
  }, [
    editMode,
    firstUnpaidOccurrenceDate,
    forceSingleEdit,
    selectedOccurrenceDate,
    transaction?.parentId,
  ]);

  const groupedAccounts = React.useMemo(() => {
    const groups = {};
    const ensureGroup = (type) => {
      if (!groups[type]) {
        groups[type] = [];
      }
      return groups[type];
    };

    Object.entries(accounts || {}).forEach(([id, account]) => {
      if (!account || account.archived) return;
      const type = account.type || 'Other';
      ensureGroup(type).push({ id, name: account.name });
    });

    const addArchivedSelection = (accountId) => {
      if (!accountId) return;
      const archivedAccount = accounts?.[accountId];
      if (!archivedAccount || !archivedAccount.archived) return;
      const archivedGroup = ensureGroup('Archived');
      const alreadyPresent = archivedGroup.some((entry) => entry.id === accountId);
      if (!alreadyPresent) {
        archivedGroup.push({
          id: accountId,
          name: `${archivedAccount.name || 'Archived Account'} (Archived)`,
        });
      }
    };

    addArchivedSelection(transaction?.fromAccount);
    addArchivedSelection(transaction?.toAccount);
    addArchivedSelection(fromAccount);
    addArchivedSelection(toAccount);

    Object.keys(groups).forEach((type) => {
      groups[type].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })
      );
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === 'Archived') return 1;
        if (b === 'Archived') return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, type) => {
        acc[type] = groups[type];
        return acc;
      }, {});
  }, [accounts, transaction, fromAccount, toAccount]);

  const handleSubmit = (e) => {
    e.preventDefault();

    onSave({
      ...transaction,
      description,
      amount: parseFloat(amount),
      fromAccount,
      toAccount,
      // Use plain `YYYY-MM-DD` strings so recurrence calculations work correctly
      date,
      recurrence,
      recurrenceEndDate: recurrenceEndDate || '',
      recurrenceInterval: parseInt(recurrenceInterval, 10) || 1,
      isPaid,
      editMode: forceSingleEdit ? 'single' : editMode,
    });
  };

  const handleRecurrenceChange = (e) => {
    setRecurrence(e.target.value);
  };

  const handleFromAccountChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setActiveAccountField('from');
      setIsAddAccountModalOpen(true);
    } else {
      setFromAccount(value);
    }
  };

  const handleToAccountChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setActiveAccountField('to');
      setIsAddAccountModalOpen(true);
    } else {
      setToAccount(value);
    }
  };

  if (!isOpen || !transaction) return null;

  return (
    <>
    <ModalContainer>
      <ModalContent>
        <Form onSubmit={handleSubmit}>
          <Label>Description:</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} required />
          <Label>Amount:</Label>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <Label>Date:</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          <Label>From Account:</Label>
          <Select value={fromAccount} onChange={handleFromAccountChange} required>
            <option value="">Select Account</option>
            {Object.keys(groupedAccounts).map(type => (
              <OptionGroup key={type} label={type}>
                {groupedAccounts[type].map(acc => (
                  <Option key={acc.id} value={acc.id}>{acc.name}</Option>
                ))}
              </OptionGroup>
            ))}
            <Option value="add_new">+ Add Account</Option>
          </Select>
          <Label>To Account:</Label>
          <Select value={toAccount} onChange={handleToAccountChange} required>
            <option value="">Select Account</option>
            {Object.keys(groupedAccounts).map(type => (
              <OptionGroup key={type} label={type}>
                {groupedAccounts[type].map(acc => (
                  <Option key={acc.id} value={acc.id}>{acc.name}</Option>
                ))}
              </OptionGroup>
            ))}
            <Option value="add_new">+ Add Account</Option>
          </Select>
          {allowPaidStatusEdit && (
            <>
              <Label>
                <input
                  type="checkbox"
                  checked={isPaid}
                  onChange={(e) => setIsPaid(e.target.checked)}
                />
                Paid
              </Label>
            </>
          )}
          {transaction.parentId && !forceSingleEdit && (
            <>
              <Label>Edit Mode:</Label>
              <Select value={editMode} onChange={(e) => setEditMode(e.target.value)}>
                <Option value="single">Single Occurrence</Option>
                <Option value="series">Entire Series</Option>
              </Select>
            </>
          )}
          {editMode === 'series' && (
            <>
              <Label>Recurrence:</Label>
              <Select value={recurrence} onChange={handleRecurrenceChange}>
                <Option value="">None</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
                <Option value="annually">Annually</Option>
                <Option value="custom">Custom</Option>
              </Select>
              {recurrence && recurrence !== 'custom' && (
                <>
                  <Label>
                    Every {recurrence === 'weekly' ? 'X Weeks' : recurrence === 'monthly' ? 'X Months' : 'X Years'}:
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(e.target.value)}
                    required
                  />
                </>
              )}
              {recurrence && (
                <>
                  <Label>Recurrence End Date:</Label>
                  <Input type="date" value={recurrenceEndDate} onChange={(e) => setRecurrenceEndDate(e.target.value)} required />
                </>
              )}
              {recurrence === 'custom' && (
                <>
                  <Label>Recurrence Interval (days):</Label>
                  <Input
                    type="number"
                    min="1"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(e.target.value)}
                    required
                  />
                </>
              )}
            </>
          )}
          <Button type="submit">Save Changes</Button>
          <Button onClick={onClose}>Close</Button>
        </Form>
      </ModalContent>
    </ModalContainer>
    <AddAccountModal
      isOpen={isAddAccountModalOpen}
      onClose={() => setIsAddAccountModalOpen(false)}
      onSave={async (account) => {
        const newId = await addAccount(userId, account);
        if (activeAccountField === 'from') {
          setFromAccount(newId);
        } else if (activeAccountField === 'to') {
          setToAccount(newId);
        }
        setIsAddAccountModalOpen(false);
      }}
    />
    </>
  );
};

export default EditTransactionModal;
